import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import Layout from "../Layout/Layout";
import { getOptimizedStories, getStoryCategories } from "./utilities/ApiCalls";
import "../css/Stories.css";
import Context from "../Context";
import styled, { createGlobalStyle } from "styled-components";
import { setPageTitle } from "../utils/titleUtils";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import Constant from "./Constant";
import ProfileImage from "../Componets/ProfileImage";
import SindhiFontWoff2 from "../fonts/SHREE-DEV7-0714.woff2";
import SindhiFontWoff from "../fonts/SHREE-DEV7-0714.woff";

const SindhiFontStyles = createGlobalStyle`
  @font-face {
    font-family: 'SHREE-DEV7-0714';
    src:
      url(${SindhiFontWoff2}) format('woff2'),
      url(${SindhiFontWoff}) format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;
const Stories = (props) => {
  const location = useLocation();

  useEffect(() => {
    setPageTitle(location.pathname);
  }, [location]);
  const history = useHistory();
  const { state } = useContext(Context);
  const query = new URLSearchParams(props.location.search);
  let search = query.get("search");

  const { t } = useTranslation();

  const [stories, setStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const observer = useRef();
  const lastStoryElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNo((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    let params = { moduleType: 1 };
    getStoryCategories(params).then((response) => {
      setCategories(response.data);
    });
  }, []);

  // Reset state when category changes
  const handleCategoryChange = (category) => {
    setCategoryName(category);
    setStories([]);
    setPageNo(1);
    setHasMore(true);
  };

  useEffect(() => {
    fetchStories();
  }, [categoryName, pageNo, search]);

  const fetchStories = async () => {
    try {
      setLoading(true);
      let data = {
        pageNo: pageNo,
        categoryName: categoryName,
      };

      if (search) {
        data.search = search;
      }

      const response = await getOptimizedStories(data);
      const responseData = response || {};
      const newStories = responseData.data || [];
      const pagination = responseData.pagination;

      if (newStories.length === 0) {
        setHasMore(false);
      } else {
        setStories((prevStories) => {
          // For category change, replace stories
          if (pageNo === 1) {
            return newStories;
          }

          // For pagination, append unique stories
          const uniqueStories = newStories.filter(
            (newStory) =>
              !prevStories.some((story) => story._id === newStory._id)
          );
          return [...prevStories, ...uniqueStories];
        });

        // Update hasMore based on pagination info if available
        if (pagination && typeof pagination.totalPages === "number") {
          if (pageNo >= pagination.totalPages) {
            setHasMore(false);
          }
        } else if (newStories.length < 10) {
          // Fallback: if fewer than default limit returned, no more pages
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error("Error fetching stories:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const proxiedUrl = (url) => {
    if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com"))
      return url;
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return "";
    const plainText = text.replace(/<[^>]+>/g, "");
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + "...";
  };

  return (
    <Layout>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "transparent",
        }}
      >
        <Root>
          <SindhiFontStyles />
          <div className="stories-container">
            <div className="header">
              <div className="stories">{t("Stories.Stories")}</div>
              <div className="select">
                <select
                  id="categoryDropdown"
                  value={categoryName}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="drop-down"
                >
                  <option value="">Genres</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid-container">
              {stories.map((data, index) => {
                const isLastStory = stories.length === index + 1;
                const isSindhiStory =
                  (data.language || "").toLowerCase() === "sindhi";
                return (
                  <Link
                    key={data._id}
                    ref={isLastStory ? lastStoryElementRef : null}
                    to={`/story-details/${data._id}`}
                    style={{ textDecoration: "none" }}
                  >
                    <div className="cart">
                      <div className="author-info">
                        <ProfileImage
                          src={data.profilePic || data.user?.profilePic}
                          name={data.authorName || data.user?.name || "Author"}
                          size={40}
                          className="author-avatar"
                          alt={data.authorName || data.user?.name || "Author"}
                          proxiedUrl={proxiedUrl}
                          imgStyle={{
                            border: "1px solid var(--color-border)",
                            background: "var(--color-background)",
                          }}
                          avatarProps={{
                            border: "1px solid var(--color-border)",
                            backgroundColor: "var(--color-background)",
                          }}
                        />
                        <span className="author-name">
                          {data.authorName || data.user?.name || "Unknown"}
                        </span>
                      </div>
                      <div
                        className="title"
                        style={
                          isSindhiStory
                            ? {
                                fontFamily: "SHREE-DEV7-0714",
                                fontSize: "16px",
                              }
                            : undefined
                        }
                      >
                        {data.name}
                      </div>
                      {(data.description || data.story) && (
                        <div
                          className={`story-snippet${
                            isSindhiStory ? " sindhi-text" : ""
                          }`}
                          dangerouslySetInnerHTML={{
                            __html: truncateText(
                              data.description || data.story,
                              150
                            ),
                          }}
                        ></div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>

            {stories.length === 0 && !loading && (
              <div style={{ textAlign: "center", color: "var(--color-text)" }}>
                No Stories Found
              </div>
            )}

            {loading && (
              <div style={{ textAlign: "center", color: "var(--color-text)" }}>
                {t("Loading")}
              </div>
            )}

            {/* {!hasMore && stories.length > 0 && (
              <div style={{ textAlign: "center", color: "white" }}>
                No more stories to load
              </div>
            )} */}
          </div>
        </Root>
      </div>
    </Layout>
  );
};

export default Stories;

const Root = styled.div`
  min-height: 100vh;
  background-color: transparent;
  height: auto;
  width: 100%;

  display: flex;
  max-width: 1300px;

  .stories {
    color: var(--color-text-tertiary, var(--color-text));
    font-size: 25px;
    font-weight: bold;
  }

  .drop-down {
    padding: 4px;
    min-width: 100px;
    width: auto;
    border-radius: 20px;
    color: var(--color-primary);
  }

  .header {
    width: 100%;
    justify-content: space-between;
    padding-left: 30px;
    padding-right: 30px;
    display: flex;
    align-items: center;
    margin-top: 40px;
    margin-bottom: 10px;
    @media (min-width: 1100px) {
      max-width: 1300px;
      padding-right: 75px;
    }
  }

  .cart {
    width: 100%;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: 8px;
    padding: 24px;
    transition: transform 0.3s ease, box-shadow 0.3s ease,
      border-color 0.3s ease;
    display: flex;
    flex-direction: column;
    height: 100%;
    cursor: pointer;
    text-align: left;
    color: var(--color-text-tertiary);
    position: relative;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
      border-color: var(--color-primary);
    }
  }

  .author-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }

  .author-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid var(--color-border);
    background: var(--color-background);
  }

  .author-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-tertiary);
    font-family: sans-serif;
  }

  .title {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-tertiary);
    margin: 0;
    line-height: 1.3;
    font-family: "Georgia", serif;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-align: left;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    padding-bottom: 12px;
    margin-bottom: 16px;
    border-bottom: 1px solid var(--color-border);
  }

  .story-snippet {
    font-size: 14px;
    line-height: 1.6;
    color: var(--color-text-tertiary);
    font-family: "Georgia", serif;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-align: left;

    &::first-letter {
      float: left;
      font-size: 48px;
      line-height: 0.7;
      padding-right: 8px;
      padding-top: 4px;
      font-weight: 400;
      font-style: italic;
      color: var(--color-text-tertiary);
    }

    p {
      margin: 0;
      display: inline;
    }
  }

  .story-snippet.sindhi-text,
  .story-snippet.sindhi-text p,
  .story-snippet.sindhi-text strong,
  .story-snippet.sindhi-text em,
  .story-snippet.sindhi-text u {
    font-family: "SHREE-DEV7-0714", "Merriweather", serif;
    font-size: 16px;
  }

  .grid-container {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    padding: 10px 10px;
    background-color: var(--color-raw-porcelain);

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
      padding: 0 4px;
    }
  }

  .btn {
    margin-top: 25px;
    background-color: #bd2130;
    color: white;
    font-weight: semibold;
    border-radius: 20px;
  }
  .adventure-title {
    color: var(--color-text-tertiary, var(--color-text));
    font-size: 30px;
    font-family: "Georgia, serif";
  }

  .sub-title {
    color: var(--color-text-tertiary, var(--color-text));
    font-family: "Georgia, serif";
    @media (min-width: 350px) and (max-width: 400px) {
      font-size: 12px;
      letter-spacing: 0.3px;
    }
  }

  .stories-container {
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    background: transparent;
    padding-bottom: 40px;
  }
`;
